import React, { useRef } from "react";
import Swal from "sweetalert2";
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from "@mui/material";
import { useSelector } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";
import { sendMail } from "../../../../api/buzon/BuzonService";
import { getOrganizationId } from "../../../../utils/session.utils";

const Write = () => {
  const agencieSelected = useSelector(
    (state) => state.myAgencies?.selected_agencie_to_consult
  );
  const userName = useSelector((state) => state.session?.decodedToken?.name ?? "");
  const [subject, setSubject] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [attachments, setAttachments] = React.useState([]);
  const [mailboxKindId, setMailboxKindId] = React.useState(0);
  const fileInputRef = useRef(null);
  const subjectMaxLength = 30;
  const messageMaxLength = 1000;

  const handleFileChange = (e) => {
    const filesArray = Array.from(e.target.files);
    setAttachments(filesArray);
    e.target.value = null; // Permite volver a seleccionar el mismo archivo
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const mailbox = {
      id: 0,
      organizationId: getOrganizationId(),
      mailboxKindId: mailboxKindId || 0,
      subject: subject || "",
      body: message || "",
      fromAgencyId: agencieSelected.id,
      broadcast: false,
      enabled: true,
      created: new Date().toISOString(),
      deleted: null,
      fileName: attachments.length > 0 ? attachments[0].name : "",
      userName: userName,
    };

    const formData = new FormData();
    formData.append("mailbox", JSON.stringify(mailbox));
    formData.append("agencyList", JSON.stringify([]));
    if (attachments && Array.isArray(attachments) && attachments.length > 0) {
      formData.append("file", attachments[0]); // Solo se envía el primer archivo
    }

    sendMail(formData)
      .then((response) => {
        if (response.status === 200) {
          Swal.fire({
            icon: "success",
            title: "Correo enviado",
            text: "El correo se envió correctamente.",
          }).then(() => {
            // Limpiar el formulario después de cerrar el Swal
            setSubject("");
            setMessage("");
            setAttachments([]);
            setMailboxKindId(null);
          });
        }
      })
      .catch((error) => {
        console.error("Error al enviar correo:", error);
        if (Swal) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Hubo un error al enviar el correo.",
          });
        }
      });
  };

  return (
    <Box sx={{ flex: 1, p: 3, overflowY: "auto" }}>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h6" gutterBottom>
          Redactar
        </Typography>
      </Box>
      <form onSubmit={handleSubmit}>
        <Box mb={2}>
          <FormControl fullWidth>
            <Typography variant="body1" gutterBottom>
              Categoría:
            </Typography>
            <Select
              value={mailboxKindId === null ? "" : mailboxKindId}
              onChange={(e) => setMailboxKindId(Number(e.target.value))}
              required
              fullWidth
              variant="outlined"
              sx={{ mb: 1 }}
            >
              <MenuItem value={0}>Varios</MenuItem>
              <MenuItem value={2}>Documentación</MenuItem>
              <MenuItem value={5}>Solicitudes</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box mb={2}>
          <FormControl fullWidth variant="outlined" sx={{ mb: 1 }}>
            <Typography variant="body1" gutterBottom>
              Asunto:
            </Typography>
            <TextField
              type="text"
              value={subject}
              onChange={(e) => {
                if (e.target.value.length <= subjectMaxLength) {
                  setSubject(e.target.value);
                } 
              }}
              required
              sx={{ mb: 1 }}
              placeholder="Asunto"
              helperText={`Máximo ${subjectMaxLength} caracteres. Te quedan ${subjectMaxLength - subject.length}`}
              inputProps={{
                maxLength: subjectMaxLength,
              }}
            />
          </FormControl>
        </Box>
        <Box mb={2}>
          <FormControl fullWidth variant="outlined" sx={{ mb: 1 }}>
            <Typography variant="body1" gutterBottom>
              Mensaje:
            </Typography>
            <TextField
              multiline
              value={message}
              onChange={(e) => {
                if (e.target.value.length <= messageMaxLength) {
                  setMessage(e.target.value)
                }
              }}
              required
              rows={6}
              sx={{ width: "100%", mb: 1 }}
              maxLength={messageMaxLength}
              placeholder="Escribe tu mensaje aquí"
            />
            <Typography variant="caption" display="block" align="right">
              Máximo {messageMaxLength} caracteres. Te quedan{" "}
              {messageMaxLength - message.length}
            </Typography>
          </FormControl>
        </Box>
        <Box mb={2}>
          <Typography variant="body1" gutterBottom>
            Adjuntos:
          </Typography>
          <input
            type="file"
            multiple
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
            <Button
              variant="outlined"
              onClick={() => fileInputRef.current.click()}
            >
              Seleccionar archivos
            </Button>
            {attachments.map((file, idx) => (
              <Box
                key={idx}
                display="flex"
                alignItems="center"
                border={1}
                borderColor="grey.300"
                borderRadius={2}
                px={1.5}
                py={0.5}
                sx={{ backgroundColor: "#f5f5f5" }}
              >
                <Typography variant="body2" sx={{ mr: 1 }}>
                  {file.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveAttachment(idx)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button type="submit" variant="contained" color="primary">
            Enviar
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default Write;
